import { useNotificationsContext } from "core/state/NotificationsContext/NotificationsContext";
import { useCallback, useEffect, useMemo } from "react";
import { useNavigate, useLocation } from "react-router-dom";
import useComponentVisible from "shared/UI/Hooks/useComponentVisible";
import { useMatchMedia } from "shared/UI/Hooks/useMatchMedia";
import { ResponsiveBreakpoints } from "shared/UI/ResponsiveBreakpoints";
import { Badge } from "shared/UI/Badge/Badge";
import { NotificationsModule } from "UIPalette/NotificationsModule/NotificationsModule";

import { ReactComponent as OutlineBellIcon } from "@icons/bell.svg";
import { ReactComponent as FilledBellIcon } from "@icons/bell-filled.svg";
import { NavBarMenuOverlay } from "shared/UI/LayoutComponents/LayoutComponents";
import { useTranslation } from "react-i18next";

export default function NavBarNotifications() {
  const navigate = useNavigate();
  const location = useLocation();
  const { t } = useTranslation(undefined, { keyPrefix: "notifications" });

  const {
    targetAreaRef: notificationsModuleRef,
    isComponentVisible: isNotificationsModuleVisible,
    setIsComponentVisible: setIsNotificationsModuleVisible,
  } = useComponentVisible<HTMLDivElement>(false);

  const handleHideNotificationsModule = useCallback(
    () => setIsNotificationsModuleVisible(false),
    [setIsNotificationsModuleVisible]
  );

  const isLargerDisplay = useMatchMedia(ResponsiveBreakpoints.Desktop);

  const { hasNewNotifications } = useNotificationsContext();

  const BellIcon = useMemo(() => {
    const moduleOpen = isNotificationsModuleVisible || location.pathname === "/notifications";
    return moduleOpen ? FilledBellIcon : OutlineBellIcon;
  }, [isNotificationsModuleVisible, location.pathname]);

  const handleBellClick = useCallback(() => {
    if (!isLargerDisplay) {
      navigate("/notifications");
      return;
    }
    setIsNotificationsModuleVisible((prev) => !prev);
  }, [isLargerDisplay, navigate, setIsNotificationsModuleVisible]);

  useEffect(() => {
    if (!isLargerDisplay) {
      setIsNotificationsModuleVisible(false);
    }
  }, [setIsNotificationsModuleVisible, isLargerDisplay]);

  return (
    <div ref={notificationsModuleRef}>
      <Badge role="status" data-testid="notification-badge" aria-hidden={!hasNewNotifications} aria-label={t("newNotificationAriaLabel")} hidden={!hasNewNotifications} className="ml-4 lg:ml-2 lg:mr-5" offsetX={7} offsetY={7}>
        <BellIcon
          className="text-brand-purple cursor-pointer"
          onClick={handleBellClick}
        />
      </Badge>

      {isNotificationsModuleVisible && (
        <NavBarMenuOverlay className="right-12 w-[400px]">
          <div className="flex flex-col h-[600px] max-h-[80vh]">
            <NotificationsModule
              renderWithContainer={false}
              hideComponent={handleHideNotificationsModule}
            />
          </div>
        </NavBarMenuOverlay>
      )}
    </div>
  );
}
